import { useCallback, useEffect, useId, useRef, useState } from "react";
import { IconPtaszek, IconStrzalkaDol } from "../icons/index.js";
import { useAnchoredPanel } from "./useAnchoredPanel.js";

/**
 * F-6: lista rozwijana. Zamknieta wyglada jak kazde pole (F-5), rozwinieta to panel
 * na --surface z cieniem, opcja wybrana oznaczona PTASZKIEM (A-7: nie samym kolorem).
 *
 * Napisane od nowa, nie przepisane z gotowca. Klawiatura w pickers.html i
 * form-controls.html jest ZEPSUTA: opcje maja tabindex="-1", ale nic nie wola na nich
 * .focus(), wiec handler keydown podpiety do listy nigdy sie nie odpala, a ArrowDown
 * przy otwartej liscie wpada w galaz Enter/Spacji i nie robi nic. Tutaj fokus ZOSTAJE
 * na wyzwalaczu (wzorzec combobox tylko-do-wyboru), a aktywna opcje wskazuje
 * aria-activedescendant - dokladnie po to ten atrybut istnieje (A-4, A-5).
 */

export interface OpcjaSelect<T extends string = string> {
  wartosc: T;
  etykieta: string;
  /** Druga linia opcji - np. skrotowy opis modelu. Nieobowiazkowa. */
  opis?: string;
}

interface Props<T extends string> {
  id: string;
  wartosc: T;
  opcje: readonly OpcjaSelect<T>[];
  onZmiana: (wartosc: T) => void;
  /** CP-4: tekst zastepczy, gdy wartosc nie pasuje do zadnej opcji. */
  placeholder?: string;
  /**
   * Zmiana wartosci niedostepna (np. trwa usuwanie rekordu, ktorego dotyczy lista). Jak w Button (B-5): aria-disabled
   * zamiast natywnego `disabled`, bo ten przenosi fokus z wyzwalacza na <body>. Lista nie otwiera sie, klawisze nic nie zmieniaja.
   */
  wylaczony?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
  "aria-label"?: string;
}

/** Bufor typeahead kasuje sie po tej przerwie - tyle, co w natywnym <select>. */
const PRZERWA_TYPEAHEAD = 500;

export function Select<T extends string>({
  id,
  wartosc,
  opcje,
  onZmiana,
  placeholder = "Wybierz",
  wylaczony = false,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  "aria-label": ariaLabel,
}: Props<T>): React.JSX.Element {
  const [otwarty, setOtwarty] = useState(false);
  const [aktywny, setAktywny] = useState(0);
  const korzenRef = useRef<HTMLDivElement>(null);
  const wyzwalaczRef = useRef<HTMLButtonElement>(null);
  const listaRef = useRef<HTMLUListElement>(null);
  const buforRef = useRef("");
  const buforTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const bazaId = useId();
  const idListy = `${bazaId}-lista`;
  const idOpcji = (i: number): string => `${bazaId}-opcja-${i}`;

  const indeksWybranej = opcje.findIndex((o) => o.wartosc === wartosc);
  const wybrana = indeksWybranej >= 0 ? opcje[indeksWybranej] : undefined;

  const { styl } = useAnchoredPanel({
    otwarty,
    kotwicaRef: wyzwalaczRef,
    panelRef: listaRef,
    preferowanaStrona: "pod",
    wyrownanie: "start",
    dopasujSzerokosc: true,
    // 6px - tyle, ile ma `top: calc(100% + 6px)` w gotowcu form-controls.html.
    odstep: 6,
  });

  const otworz = useCallback(
    (start: "wybrana" | "pierwsza" | "ostatnia") => {
      const i =
        start === "pierwsza"
          ? 0
          : start === "ostatnia"
            ? opcje.length - 1
            : indeksWybranej >= 0
              ? indeksWybranej
              : 0;
      setAktywny(Math.max(0, i));
      setOtwarty(true);
    },
    [indeksWybranej, opcje.length],
  );

  const zamknij = useCallback(() => {
    setOtwarty(false);
  }, []);

  const wybierz = useCallback(
    (i: number) => {
      const opcja = opcje[i];
      if (opcja) onZmiana(opcja.wartosc);
      setOtwarty(false);
      wyzwalaczRef.current?.focus();
    },
    [opcje, onZmiana],
  );

  // Klik poza kontrolka zamyka liste. Bez zwracania fokusu - uzytkownik wlasnie
  // wskazal mysza, gdzie chce byc.
  useEffect(() => {
    if (!otwarty) return;
    function naDokumencie(e: MouseEvent): void {
      if (!korzenRef.current?.contains(e.target as Node)) setOtwarty(false);
    }
    document.addEventListener("mousedown", naDokumencie);
    return () => document.removeEventListener("mousedown", naDokumencie);
  }, [otwarty]);

  // Aktywna opcja musi byc widoczna, gdy lista sie przewija.
  useEffect(() => {
    if (!otwarty) return;
    const el = document.getElementById(idOpcji(aktywny));
    // jsdom nie implementuje scrollIntoView - bez tej straznicy testy padaja na TypeError.
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
    // idOpcji jest stabilne w obrebie instancji (bazaId z useId).
  }, [otwarty, aktywny, bazaId]);

  useEffect(() => {
    return () => {
      if (buforTimerRef.current !== null) clearTimeout(buforTimerRef.current);
    };
  }, []);

  function typeahead(znak: string): void {
    buforRef.current += znak.toLowerCase();
    if (buforTimerRef.current !== null) clearTimeout(buforTimerRef.current);
    buforTimerRef.current = setTimeout(() => {
      buforRef.current = "";
    }, PRZERWA_TYPEAHEAD);

    const i = opcje.findIndex((o) => o.etykieta.toLowerCase().startsWith(buforRef.current));
    if (i < 0) return;
    if (otwarty) setAktywny(i);
    else onZmiana(opcje[i].wartosc);
  }

  function naKlawisz(e: React.KeyboardEvent<HTMLButtonElement>): void {
    // Wylaczony wyzwalacz nic nie robi, ale Tab dziala jak zwykle (bez preventDefault).
    if (wylaczony) return;
    const ostatnia = opcje.length - 1;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!otwarty) otworz("wybrana");
        else setAktywny((i) => Math.min(ostatnia, i + 1));
        return;
      case "ArrowUp":
        e.preventDefault();
        if (e.altKey && otwarty) {
          zamknij();
          return;
        }
        if (!otwarty) otworz("wybrana");
        else setAktywny((i) => Math.max(0, i - 1));
        return;
      case "Home":
        e.preventDefault();
        if (!otwarty) otworz("pierwsza");
        else setAktywny(0);
        return;
      case "End":
        e.preventDefault();
        if (!otwarty) otworz("ostatnia");
        else setAktywny(ostatnia);
        return;
      case "Enter":
      case " ":
        e.preventDefault();
        if (!otwarty) otworz("wybrana");
        else wybierz(aktywny);
        return;
      case "Escape":
        if (!otwarty) return;
        e.preventDefault();
        zamknij();
        wyzwalaczRef.current?.focus();
        return;
      case "Tab":
        // Bez preventDefault: A-4 wymaga, zeby Tab wyprowadzal fokus dalej.
        if (otwarty) zamknij();
        return;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          typeahead(e.key);
        }
    }
  }

  return (
    <div className={`select${otwarty ? " is-open" : ""}`} ref={korzenRef}>
      <button
        type="button"
        id={id}
        ref={wyzwalaczRef}
        className="select__trigger"
        role="combobox"
        aria-expanded={otwarty}
        aria-controls={idListy}
        aria-haspopup="listbox"
        aria-activedescendant={otwarty ? idOpcji(aktywny) : undefined}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        aria-disabled={wylaczony || undefined}
        onClick={() => {
          if (wylaczony) return;
          if (otwarty) zamknij();
          else otworz("wybrana");
        }}
        onKeyDown={naKlawisz}
      >
        <span className="select__value">{wybrana ? wybrana.etykieta : placeholder}</span>
        <IconStrzalkaDol size={16} className="select__chevron" />
      </button>

      <ul className="select__list" id={idListy} ref={listaRef} role="listbox" hidden={!otwarty} style={styl}>
        {opcje.map((opcja, i) => (
          <li
            key={opcja.wartosc}
            id={idOpcji(i)}
            role="option"
            aria-selected={opcja.wartosc === wartosc}
            className={`select__option${i === aktywny ? " is-focused" : ""}`}
            // Mysz: mousedown zamiast click, zeby wyprzedzic straznika "klik poza".
            onMouseDown={(e) => {
              e.preventDefault();
              if (!wylaczony) wybierz(i);
            }}
            onMouseEnter={() => setAktywny(i)}
          >
            <span className="select__option-tresc">
              {opcja.etykieta}
              {opcja.opis && <span className="select__option-opis">{opcja.opis}</span>}
            </span>
            <IconPtaszek size={16} className="select__check" />
          </li>
        ))}
      </ul>
    </div>
  );
}
